import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import { FileText, CreditCard, Shield } from 'lucide-react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { format, parseISO, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval } from 'date-fns';

const CalendarPage = () => {
  const { token } = useAuth();
  const [renewals, setRenewals] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRenewals = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/renewals`, { 
          headers,
          withCredentials: true 
        });
        setRenewals(response.data);
      } catch (error) {
        console.error('Error fetching renewals:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchRenewals();
  }, [token]);

  const getRenewalsForDate = (date) => {
    return renewals.filter(r => isSameDay(parseISO(r.renewal_date), date));
  };

  const selectedDateRenewals = getRenewalsForDate(selectedDate);

  // Get dates that have renewals for highlighting
  const renewalDates = renewals.map(r => parseISO(r.renewal_date));

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'document': return <FileText className="w-4 h-4" />;
      case 'subscription': return <CreditCard className="w-4 h-4" />;
      case 'insurance': return <Shield className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  // Custom day render to show dots for days with renewals
  const modifiers = {
    hasRenewal: renewalDates
  };

  const modifiersStyles = {
    hasRenewal: {
      fontWeight: 'bold'
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ink"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="calendar-page">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-ink font-display">Calendar</h1>
          <p className="text-ink-600 mt-1">View all your renewals by date</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2">
            <Card className="bg-white border border-ink-200 shadow-sm">
              <CardContent className="p-6">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  modifiers={modifiers}
                  modifiersStyles={modifiersStyles}
                  className="rounded-md w-full"
                  classNames={{
                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month: "space-y-4 w-full",
                    caption: "flex justify-center pt-1 relative items-center",
                    caption_label: "text-lg font-semibold text-ink",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-9 w-9 bg-transparent p-0 opacity-50 hover:opacity-100 border border-ink-200 rounded-md",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse space-y-1",
                    head_row: "flex w-full",
                    head_cell: "text-ink-600 rounded-md w-full font-medium text-xs uppercase tracking-wide",
                    row: "flex w-full mt-2",
                    cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 w-full h-12",
                    day: "h-12 w-full p-0 font-normal aria-selected:opacity-100 hover:bg-cream rounded-md transition-colors",
                    day_range_end: "day-range-end",
                    day_selected: "bg-ink text-white hover:bg-black hover:text-white focus:bg-ink focus:text-white",
                    day_today: "bg-cream text-ink",
                    day_outside: "day-outside text-ink-600/70 opacity-50",
                    day_disabled: "text-ink-600/70 opacity-50",
                    day_hidden: "invisible",
                  }}
                  components={{
                    DayContent: ({ date }) => {
                      const dayRenewals = getRenewalsForDate(date);
                      return (
                        <div className="relative w-full h-full flex flex-col items-center justify-center">
                          <span>{date.getDate()}</span>
                          {dayRenewals.length > 0 && (
                            <div className="absolute bottom-1 flex gap-0.5">
                              {dayRenewals.slice(0, 3).map((_, i) => (
                                <div 
                                  key={i} 
                                  className="w-1.5 h-1.5 rounded-full bg-respondent"
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }
                  }}
                />
              </CardContent>
            </Card>
          </div>

          {/* Selected Date Renewals */}
          <div>
            <Card className="bg-white border border-ink-200 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-ink font-display">
                  {format(selectedDate, 'MMMM d, yyyy')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedDateRenewals.length === 0 ? (
                  <div className="text-center py-8 text-ink-600">
                    <p>No renewals on this date</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedDateRenewals.map((renewal) => (
                      <div 
                        key={renewal.renewal_id}
                        className="p-3 rounded-md border border-ink-200 hover:border-ink-200 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                            renewal.category === 'document' ? 'bg-cream text-respondent' :
                            renewal.category === 'subscription' ? 'bg-emerald-50 text-emerald-600' :
                            'bg-amber-50 text-amber-600'
                          }`}>
                            {getCategoryIcon(renewal.category)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-ink truncate">{renewal.title}</p>
                            <p className="text-sm text-ink-600 capitalize">{renewal.category}</p>
                          </div>
                        </div>
                        {renewal.description && (
                          <p className="text-sm text-ink-600 mt-2 truncate">{renewal.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upcoming This Month */}
            <Card className="bg-white border border-ink-200 shadow-sm mt-6">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-semibold text-ink font-display">
                  This Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                {(() => {
                  const monthStart = startOfMonth(selectedDate);
                  const monthEnd = endOfMonth(selectedDate);
                  const monthRenewals = renewals.filter(r => {
                    const date = parseISO(r.renewal_date);
                    return date >= monthStart && date <= monthEnd;
                  }).sort((a, b) => parseISO(a.renewal_date).getTime() - parseISO(b.renewal_date).getTime());

                  if (monthRenewals.length === 0) {
                    return (
                      <p className="text-center py-4 text-ink-600">No renewals this month</p>
                    );
                  }

                  return (
                    <div className="space-y-2">
                      {monthRenewals.slice(0, 5).map((renewal) => (
                        <div 
                          key={renewal.renewal_id}
                          className="flex items-center justify-between py-2 border-b border-ink-200 last:border-0"
                        >
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${
                              renewal.category === 'document' ? 'bg-respondent' :
                              renewal.category === 'subscription' ? 'bg-emerald-600' :
                              'bg-amber-600'
                            }`} />
                            <span className="text-sm font-medium text-ink truncate max-w-[150px]">
                              {renewal.title}
                            </span>
                          </div>
                          <span className="text-xs text-ink-600">
                            {format(parseISO(renewal.renewal_date), 'MMM d')}
                          </span>
                        </div>
                      ))}
                      {monthRenewals.length > 5 && (
                        <p className="text-sm text-ink-600 text-center pt-2">
                          +{monthRenewals.length - 5} more
                        </p>
                      )}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CalendarPage;
